import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Configure API base URL from environment variable
// In production, use relative URLs which will use the same domain
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isProduction ? '' : 'http://localhost:5000');
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = false; // Set to false for token-based auth
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

console.log('API Base URL:', API_BASE_URL || '(relative)');

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup axios interceptor for authentication
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUserProfile();
    } else {
      setLoading(false);
    }

    // Response interceptor for token refresh
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = localStorage.getItem('refresh_token');

          if (refreshToken) {
            try {
              const response = await axios.post('/api/auth/refresh', null, {
                headers: { Authorization: `Bearer ${refreshToken}` },
              });
              const { access_token } = response.data;
              localStorage.setItem('access_token', access_token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
              originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
              return axios(originalRequest);
            } catch (refreshError) {
              logout();
              return Promise.reject(refreshError);
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      setUser(response.data.user);
      setProfile(response.data.profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, role = null) => {
    try {
      console.log('Attempting login to:', `${axios.defaults.baseURL}/api/auth/login`);
      const response = await axios.post('/api/auth/login', { email, password, role });
      console.log('Login response received:', response.status);
      
      // Check if user has multiple roles
      if (response.data.multiple_roles) {
        console.log('Multiple roles available:', response.data.available_roles);
        return {
          multipleRoles: true,
          availableRoles: response.data.available_roles,
          email: response.data.email
        };
      }
      
      const { access_token, refresh_token, user, profile, available_roles } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('available_roles', JSON.stringify(available_roles || []));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(user);
      setProfile(profile);

      console.log('Login successful for:', user.email, 'Role:', user.role, 'Available roles:', available_roles);
      return { success: true, user, availableRoles: available_roles || [] };
    } catch (error) {
      console.error('Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setProfile(null);
  };

  const register = async (data, type = 'public') => {
    try {
      const endpoint = type === 'invite' ? '/api/auth/register-invite' : '/api/auth/register-public';
      const response = await axios.post(endpoint, data);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.put('/api/auth/profile', data);
      setProfile(response.data.profile);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Update failed',
      };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post('/api/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed',
      };
    }
  };

  const switchRole = async (newRole) => {
    try {
      const response = await axios.post('/api/auth/switch-role', { role: newRole });
      const { access_token, refresh_token, user, profile, available_roles } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('available_roles', JSON.stringify(available_roles || []));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(user);
      setProfile(profile);

      console.log('Switched to role:', user.role);
      return { success: true, user, availableRoles: available_roles || [] };
    } catch (error) {
      console.error('Role switch error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Role switch failed',
      };
    }
  };

  const value = {
    user,
    profile,
    loading,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    switchRole,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStudent: user?.role === 'student',
    isCoordinator: user?.role === 'coordinator',
    availableRoles: JSON.parse(localStorage.getItem('available_roles') || '[]')
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
